// The one place a growing-region form turns into an API payload.
//
// Two callers — the inline picker and the admin manager — and the backend's
// Joi schema rejects unknown keys outright, so the shape has to be built
// deliberately rather than spread from a form object. Blank inputs become
// `null`, not `''`: `centroid_latitude: ''` fails `Joi.number().allow(null)`
// with a validation error that says nothing useful to the person typing.

import type { GrowingRegion } from '../../types/cultivar';

/**
 * Matches the server's `ORDER BY country NULLS LAST, name`, so a region created
 * inline lands where a reload would put it rather than on the end of the list.
 */
export const byCountryThenName = (a: GrowingRegion, b: GrowingRegion) => {
  // U+FFFF sorts after any real country name, which is what NULLS LAST means.
  const ca = a.country ?? '￿';
  const cb = b.country ?? '￿';
  return ca === cb ? a.name.localeCompare(b.name) : ca.localeCompare(cb);
};

/** Same ISO 3166-2 shape the backend validates against. */
export const REGION_CODE_RE = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;

/** Every field is a string — it comes from an `<input>`. */
export interface GrowingRegionDraft {
  name: string;
  country: string;
  region_code: string;
  centroid_latitude: string;
  centroid_longitude: string;
  elevation_m: string;
  notes: string;
}

export interface GrowingRegionPayload {
  name: string;
  country: string | null;
  region_code: string | null;
  centroid_latitude: number | null;
  centroid_longitude: number | null;
  elevation_m: number | null;
  notes: string | null;
}

export const emptyRegionDraft: GrowingRegionDraft = {
  name: '',
  country: 'Australia',
  region_code: '',
  centroid_latitude: '',
  centroid_longitude: '',
  elevation_m: '',
  notes: '',
};

const str = (v: string | number | null | undefined) => (v == null ? '' : String(v));

export function draftFromRegion(r: GrowingRegion): GrowingRegionDraft {
  return {
    name: r.name,
    country: str(r.country),
    region_code: str(r.region_code),
    centroid_latitude: str(r.centroid_latitude),
    centroid_longitude: str(r.centroid_longitude),
    elevation_m: str(r.elevation_m),
    notes: str(r.notes),
  };
}

function numberOrNull(raw: string): number | null | undefined {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined; // undefined = unparseable
}

export type RegionPayloadResult =
  | { ok: false; error: string }
  | { ok: true; payload: GrowingRegionPayload };

/**
 * Validate a draft and shape it for the API. Returns a message meant for a
 * person, or a payload ready to send.
 *
 * Latitude and longitude are checked here as well as server-side because the
 * pair is what derives the hemisphere and what `findNearestRegion` matches a
 * GPS fix against — a silently dropped centroid costs both.
 */
export function regionPayload(d: GrowingRegionDraft): RegionPayloadResult {
  const name = d.name.trim();
  if (!name) return { ok: false, error: 'Region name is required' };
  if (name.length > 160) return { ok: false, error: 'Region name is limited to 160 characters' };

  const code = d.region_code.trim().toUpperCase();
  if (code && !REGION_CODE_RE.test(code)) {
    return { ok: false, error: 'Region code must be ISO 3166-2, e.g. AU-VIC' };
  }

  const lat = numberOrNull(d.centroid_latitude);
  if (lat === undefined) return { ok: false, error: 'Latitude must be a number' };
  if (lat !== null && (lat < -90 || lat > 90)) {
    return { ok: false, error: 'Latitude must be between -90 and 90' };
  }

  const lng = numberOrNull(d.centroid_longitude);
  if (lng === undefined) return { ok: false, error: 'Longitude must be a number' };
  if (lng !== null && (lng < -180 || lng > 180)) {
    return { ok: false, error: 'Longitude must be between -180 and 180' };
  }

  // A lone coordinate places nothing, and the hemisphere it would derive is a
  // guess dressed up as a fact.
  if ((lat === null) !== (lng === null)) {
    return { ok: false, error: 'Give both latitude and longitude, or neither' };
  }

  const elevRaw = numberOrNull(d.elevation_m);
  if (elevRaw === undefined) return { ok: false, error: 'Elevation must be a number' };
  const elevation_m = elevRaw === null ? null : Math.round(elevRaw);

  return {
    ok: true,
    payload: {
      name,
      country: d.country.trim() || null,
      region_code: code || null,
      centroid_latitude: lat,
      centroid_longitude: lng,
      elevation_m,
      notes: d.notes.trim() || null,
    },
  };
}

/** What the server will derive from this draft, so the form can say it up front. */
export function draftHemisphere(d: GrowingRegionDraft): 'N' | 'S' | null {
  const t = d.centroid_latitude.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return n < 0 ? 'S' : 'N';
}
